import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography, Button, Alert, Chip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import { useGlobalContext } from '../../hooks/useGlobalContext';
import { setActiveLandpage } from '../../store/slices/landpageManagerSlice';
import { apiRequestJson } from '../../api/apiService';

/**
 * Componente de inicialização da aplicação com contexto global.
 * Gerencia sincronização inteligente de dados baseada em timestamps.
 * Exibe progresso da sincronização e permite retry manual.
 * 
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componentes filhos a renderizar após inicialização
 * @returns {JSX.Element}
 */
export default function AppInitializer({ children }) {
  const dispatch = useDispatch();
  const {
    isLoading,
    isInitialized,
    error,
    initialize,
    forceSync,
    isFirstLoad,
    updatedData,
    errors
  } = useGlobalContext();
  
  const [retryCount, setRetryCount] = useState(0);
  const [hasGivenUp, setHasGivenUp] = useState(false);

  const MAX_RETRIES = 5;

  useEffect(() => {
    if (!isInitialized && !isLoading) {
      initialize();
    }
  }, [initialize, isInitialized, isLoading]);

  /**
   * Busca a landpage ativa e popula o slice após inicialização.
   */
  useEffect(() => {
    if (isInitialized) {
      apiRequestJson('landpage_active')
        .then((res) => {
          if (res.success && res.data) {
            dispatch(setActiveLandpage(res.data));
          }
        })
        .catch(() => {
          // Sem landpage ativa — exibe produção padrão
        });
    }
  }, [isInitialized, dispatch]);

  useEffect(() => {
    // Retry automático quando há erro
    if (error && retryCount < MAX_RETRIES && !isLoading) {
      const timer = setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        initialize();
      }, 3000);

      return () => clearTimeout(timer);
    }

    // Desiste após tentativas máximas
    if (error && retryCount >= MAX_RETRIES) {
      setHasGivenUp(true);
    }
  }, [error, retryCount, isLoading, initialize]);

  const handleReload = () => {
    window.location.reload();
  };

  const handleForceSync = async () => {
    setRetryCount(0);
    setHasGivenUp(false);
    await forceSync();
  };

  // Mostra loading durante inicialização
  if (isLoading && !hasGivenUp) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
          px: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" textAlign="center">
          {isFirstLoad ? 'Carregando dados pela primeira vez...' : 'Sincronizando dados...'}
        </Typography>
        {retryCount > 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Tentativa {retryCount} de {MAX_RETRIES}
          </Typography>
        )}
        {updatedData.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {updatedData.map((item) => (
              <Chip key={item} label={item} size="small" color="primary" />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // Mostra erro após tentativas máximas
  if (hasGivenUp && error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 3,
          px: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          Não foi possível sincronizar os dados após {MAX_RETRIES} tentativas.
        </Alert>
        <Typography variant="body1" textAlign="center" color="text.secondary">
          Verifique sua conexão com a internet e tente novamente.
        </Typography>
        {errors.length > 0 && (
          <Box sx={{ maxWidth: 500 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Erros encontrados:
            </Typography>
            {errors.map((err, index) => (
              <Typography key={index} variant="caption" display="block" color="error">
                • {err}
              </Typography>
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<SyncIcon />}
            onClick={handleForceSync}
          >
            Tentar Novamente
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={handleReload}
          >
            Recarregar Página
          </Button>
        </Box>
      </Box>
    );
  }

  // Renderiza aplicação após inicialização bem-sucedida
  return <>{children}</>;
}